import { experienceUrl } from '@/lib/urls';
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, ShoppingBag, Calendar, Gift, Search, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation } from '@/contexts/LocationContext';
import ConsumerLayout from '@/components/ConsumerLayout';
import ComingSoon from '@/components/ComingSoon';
import api from '@/lib/api';

const Marketplace = () => {
  const navigate = useNavigate();
  const { isGold } = useAuth();
  const { currentCity, currentNeighborhood } = useLocation();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const neighborhoodName = currentCity?.display || currentNeighborhood?.name || 'Denver';

  useEffect(() => {
    fetchVouchers();
  }, [currentCity]);

  const fetchVouchers = async () => {
    try {
      const cityParam = currentCity?.city ? `?city=${currentCity.city}` : '';
      const response = await api.get(`/discover/experiences${cityParam}`);
      setVouchers(response.data.experiences || response.data || []);
    } catch (error) {
      console.error('Failed to fetch vouchers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredVouchers = vouchers.filter(voucher => {
    const matchesSearch =
      voucher.title.toLowerCase().includes(search.toLowerCase()) ||
      voucher.venue_name.toLowerCase().includes(search.toLowerCase());
    
    if (filter === 'gold') {
      return matchesSearch && voucher.gold_price;
    }
    if (filter === 'date_night') {
      return matchesSearch && voucher.is_date_night;
    }
    return matchesSearch;
  });

  return (
    <ConsumerLayout darkTheme>

      {/* Hero Section */}
      <section className="relative py-16 sm:py-20 bg-gradient-to-br from-purple-900/50 via-[#0f172a] to-indigo-900/30 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(139,92,246,0.3),transparent)]" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Gift className="w-16 h-16 text-purple-400 mx-auto mb-6" />
            <h1 className="headline-serif text-4xl lg:text-5xl font-bold text-slate-50 mb-6">
              Experiences & Vouchers
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Discover exclusive experiences from {neighborhoodName}'s best venues. Book unique adventures or gift something special.
            </p>
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search experiences..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-12 h-12 bg-slate-800/50 border-slate-700 text-slate-50 placeholder:text-slate-400 focus:border-purple-500"
                data-testid="marketplace-search"
              />
            </div>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Filter Tabs */}
        <Tabs defaultValue="all" className="mb-8" onValueChange={setFilter}>
          <TabsList className="bg-slate-800 border border-slate-700">
            <TabsTrigger value="all" data-testid="filter-all" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              All Experiences
            </TabsTrigger>
            <TabsTrigger value="gold" data-testid="filter-gold" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              <Star className="w-4 h-4 mr-2 fill-current" />
              Gold Deals
            </TabsTrigger>
            <TabsTrigger value="date_night" data-testid="filter-date-night" className="data-[state=active]:bg-rose-500 data-[state=active]:text-white">
              Date Night
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500" />
          </div>
        ) : filteredVouchers.length === 0 ? (
          search ? (
            <div className="text-center py-20 bg-slate-800/50 rounded-2xl border border-slate-700">
              <Gift className="w-16 h-16 text-slate-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-50 mb-2">No experiences found</h3>
              <p className="text-slate-400 mb-6">Try a different search term</p>
              <Button variant="outline" onClick={() => setSearch('')} className="border-slate-600 text-slate-300">
                Clear Search
              </Button>
            </div>
          ) : (
            <ComingSoon
              neighborhoodName={neighborhoodName}
              neighborhoodSlug={neighborhoodSlug}
              type="experiences"
              showSuggestionCTA={true}
              showExploreOther={true}
            />
          )
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredVouchers.map((voucher, index) => {
                const displayPrice = isGold && voucher.gold_price ? voucher.gold_price : voucher.price;
                const hasSavings = isGold && voucher.gold_price && voucher.gold_price < voucher.price;
                const inventoryStatus = voucher.inventory_status || {};
                const showInventoryBadge = inventoryStatus.badge && inventoryStatus.status !== 'available';

                return (
                  <motion.div
                    key={voucher.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -4 }}
                  >
                    <Card className={`overflow-hidden bg-slate-800 border-slate-700 hover:border-purple-500/50 transition-all cursor-pointer group h-full ${inventoryStatus.status === 'sold_out' ? 'opacity-60' : ''}`}
                      onClick={() => navigate(experienceUrl(voucher))}>
                      <div className="aspect-[16/9] bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center relative overflow-hidden">
                        {voucher.images && voucher.images[0] ? (
                          <img
                            src={voucher.images[0]}
                            alt={voucher.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <Gift className="w-16 h-16 text-slate-500" />
                        )}
                        {/* Inventory Badge */}
                        {showInventoryBadge && (
                          <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold ${
                            inventoryStatus.status === 'sold_out' 
                              ? 'bg-slate-600 text-slate-300' 
                              : inventoryStatus.status === 'critical'
                              ? 'bg-red-500 text-white animate-pulse'
                              : 'bg-orange-500 text-white'
                          }`}>
                            {inventoryStatus.badge}
                          </div>
                        )}
                        {hasSavings && (
                          <div className="absolute top-3 right-3 bg-amber-400 text-black px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                            <Star className="w-3 h-3 fill-current" />
                            GOLD DEAL
                          </div>
                        )}
                        {voucher.is_date_night && !showInventoryBadge && (
                          <Badge className="absolute top-3 left-3 bg-rose-500 text-white border-0">
                            DATE NIGHT
                          </Badge>
                        )}
                      </div>
                      <CardContent className="p-6 bg-slate-800">
                        <p className="text-xs text-purple-400 font-medium mb-2">{voucher.venue_name}</p>
                        <h3 className="text-xl font-bold text-slate-50 mb-2 group-hover:text-purple-400 transition-colors line-clamp-2">
                          {voucher.title}
                        </h3>
                        <p className="text-sm text-slate-400 mb-4 line-clamp-2">
                          {voucher.description}
                        </p>
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <div className="flex items-baseline gap-2">
                              <span className="text-2xl font-bold text-slate-50">
                                ${displayPrice.toFixed(2)}
                              </span>
                              {hasSavings && (
                                <span className="text-sm text-slate-500 line-through">
                                  ${voucher.price.toFixed(2)}
                                </span>
                              )}
                            </div>
                            {hasSavings && (
                              <span className="text-xs text-emerald-400 font-medium">
                                Save ${(voucher.price - voucher.gold_price).toFixed(2)}
                              </span>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Valid until</p>
                            <p className="text-sm font-medium text-slate-300">{voucher.valid_until}</p>
                          </div>
                        </div>
                        <Button
                          className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                          onClick={() => navigate(experienceUrl(voucher))}
                          data-testid={`buy-voucher-${voucher.id}`}
                        >
                          <Gift className="w-4 h-4 mr-2" />
                          View Details
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            {/* CTA Section */}
            {!isGold && filteredVouchers.some(v => v.gold_price) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mt-16 p-8 rounded-2xl bg-gradient-to-r from-amber-900/50 to-amber-800/30 border border-amber-600/30 text-center"
              >
                <Star className="w-12 h-12 text-amber-400 mx-auto mb-4 fill-current" />
                <h2 className="headline-serif text-3xl font-bold text-slate-50 mb-4">Save More with Gold Club</h2>
                <p className="text-lg text-amber-200/80 mb-6 max-w-2xl mx-auto">
                  Get exclusive discounts on all experiences. Members save an average of $20 per purchase.
                </p>
                <Button
                  size="lg"
                  className="bg-amber-400 hover:bg-amber-500 text-black font-bold"
                  onClick={() => navigate('/gold-club')}
                  data-testid="marketplace-gold-cta"
                >
                  Join Gold Club
                </Button>
              </motion.div>
            )}
          </>
        )}
      </div>

    </ConsumerLayout>
  );
};

export default Marketplace;
